import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bookmark, CheckCircle2, ChevronRight, PlayCircle } from 'lucide-react';
import { api, type Bookmark as BookmarkItem, type BookmarksResponse } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { fmtSeconds } from '@/lib/utils';

export default function Lesezeichen() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bookmarks'],
    queryFn: () => api.get<BookmarksResponse>('/api/course/bookmarks'),
  });

  if (isLoading) {
    return <div className="mx-auto max-w-3xl p-6 text-sm text-muted-foreground">Lädt …</div>;
  }
  if (error) {
    return <div className="mx-auto max-w-3xl p-6 text-sm text-destructive">Fehler beim Laden.</div>;
  }

  const bookmarks = data?.bookmarks ?? [];

  // Server returns newest-first. Grouping preserves that order, so the course
  // whose bookmark is most recent leads the page.
  const groups: Array<{ slug: string; title: string; items: BookmarkItem[] }> = [];
  for (const b of bookmarks) {
    const existing = groups.find((g) => g.slug === b.courseSlug);
    if (existing) existing.items.push(b);
    else groups.push({ slug: b.courseSlug, title: b.courseTitle, items: [b] });
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-5 pb-40 sm:p-6 sm:pb-40 sm:space-y-8">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Startseite
      </Link>

      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Lesezeichen</h1>
        <p className="text-sm text-muted-foreground">
          Lektionen, die du dir für später gemerkt hast.
        </p>
      </header>

      {groups.length === 0 && (
        <Card className="shadow-soft">
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
              <Bookmark className="size-6 text-primary-strong" />
            </div>
            <p className="text-sm text-muted-foreground">
              Noch keine Lesezeichen. Tippe in einer Lektion auf „Merken“, um sie hier zu sammeln.
            </p>
          </CardContent>
        </Card>
      )}

      {groups.map((g) => (
        <section key={g.slug} className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-lg font-semibold">{g.title}</h2>
            <span className="shrink-0 text-xs text-muted-foreground">
              {g.items.length} Lesezeichen
            </span>
          </div>
          <div className="space-y-2">
            {g.items.map((b) => (
              <Link
                key={b.lessonId}
                to={`/kurse/${b.courseSlug}/${b.moduleId}/${b.lessonId}`}
                className="flex items-center gap-4 rounded-xl border bg-card p-4 text-card-foreground shadow-soft transition duration-200 hover:border-primary/40 hover:shadow-lift"
              >
                {b.thumbnailUrl ? (
                  <img
                    src={b.thumbnailUrl}
                    alt=""
                    className="h-14 w-20 shrink-0 rounded-md object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-md bg-muted">
                    <PlayCircle className="size-4 text-muted-foreground" />
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="line-clamp-2 text-sm font-medium leading-snug">
                    {b.lessonTitle}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {b.moduleTitle}
                    {b.durationSeconds > 0 && ` · ${fmtSeconds(b.durationSeconds)}`}
                  </div>
                </div>
                {b.completed ? (
                  <CheckCircle2 className="size-5 shrink-0 text-success" />
                ) : (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                )}
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
